import { ChartIcon } from "@signet/ui";
import { api, defineWebModule, lazyPage } from "@signet/web-kit";
import { UnclaimedNowCard, WaitingNowCard } from "./now-cards.tsx";
import { RevenueNow, RevenuePanel } from "./now.tsx";
import "./reporting.css";

// Loaded when first opened (web-kit lazy.ts). The lifted page is most of the
// app's weight on its own.
const LegacyPage = lazyPage(() => import("./legacy/page.tsx"), "default");
const SettingsPage = lazyPage(() => import("./settings.tsx"), "SettingsPage");

/* ──────────────────────────────────────────────────────────────────────────
   The web half of Reporting: the agency's earnings by creator and platform,
   drawn exactly as the legacy reporting site draws them (legacy/page.tsx).

   One route holds every view (/reporting, /reporting/creators,
   /reporting/dashboards, and the places the legacy site kept in dialogs), so
   moving between them is a change of address, not a new page: the legacy
   page keeps its filters, its dialogs and the workspace it has read, as it
   did on the legacy site. The page itself moves a person to the first view
   they may see, as the legacy page did.
   ────────────────────────────────────────────────────────────────────────── */

export default defineWebModule({
  id: "reporting",
  nav: { label: "Reporting", icon: ChartIcon, to: "/reporting", needs: "reporting.dashboard:view" },
  routes: [
    // Signet's own connections (Google sheets, Infloww), which the legacy site kept in its environment.
    { path: "/reporting/connections", component: SettingsPage, needs: "reporting.settings:view" },
    { path: "/reporting/$", component: LegacyPage, needs: "reporting.dashboard:view" },
  ],
  commands: [
    { id: "reporting.overview", label: "Reporting summary", to: "/reporting", needs: "reporting.summary:view", hint: "Earnings across every dashboard", keywords: "earnings money summary infloww overview" },
    { id: "reporting.dashboards", label: "Reporting dashboards", to: "/reporting/dashboards", needs: "reporting.dashboard:view", hint: "Reddit, IG MAIN, Twitter, SFS", keywords: "reddit twitter sfs links cards" },
    { id: "reporting.creators", label: "Creator earnings", to: "/reporting/creators", needs: "reporting.creator:view", hint: "Reporting", keywords: "creator earnings followers" },
    { id: "reporting.periods", label: "Compare periods", to: "/reporting/periods", needs: "reporting.summary:view", hint: "Any two date ranges", keywords: "week month compare" },
    { id: "reporting.import", label: "Import history", to: "/reporting/import", needs: "reporting.snapshot:view", hint: "Infloww export pairs", keywords: "xlsx upload tracking free trial snapshots" },
    { id: "reporting.reports", label: "Reporting reports", to: "/reporting/reports", needs: "reporting.report:view", hint: "Written from the numbers", keywords: "pdf report print" },
    { id: "reporting.settings", label: "Reporting settings", to: "/reporting/settings", needs: "reporting.dashboard:view", hint: "Workspace preferences", keywords: "currency timezone workspace" },
    { id: "reporting.connections", label: "Reporting connections", to: "/reporting/connections", needs: "reporting.settings:view", hint: "Google sheets and Infloww", keywords: "google service account sheet infloww" },
  ],
  nowCards: {
    "reporting.waiting": WaitingNowCard,
    "reporting.unclaimed": UnclaimedNowCard,
  },
  // Reporting addresses a creator by its own id ("little-tins"), not by the
  // roster's name, so the quick bar looks it up when the row is chosen.
  creatorPages: [
    {
      id: "reporting.creator",
      label: "Reporting",
      needs: "reporting.creator:view",
      order: 10,
      keywords: "earnings revenue numbers stats money subs",
      to: async (name) => {
        const read = await api<{ workspace: { creators?: { id: string; name: string }[] } | null }>("/api/m/reporting/workspace").catch(() => null);
        const found = read?.workspace?.creators?.find((c) => c.name.trim().toUpperCase() === name.trim().toUpperCase());
        return found ? `/reporting/creators?creator=${encodeURIComponent(found.id)}` : "/reporting/creators";
      },
    },
  ],
  nowStats: [{ id: "reporting.revenue", needs: "reporting.summary:view", order: 30, component: RevenueNow }],
  // Creators of the day folded into Revenue, which names each dashboard's top four with their amounts
  // and takes the whole row (Ben, 2026-10-29).
  nowPanels: [{ id: "reporting.revenue", needs: "reporting.summary:view", order: 30, wide: true, component: RevenuePanel }],
});
